import {
    createContext,
    Dispatch,
    FC,
    PropsWithChildren,
    Reducer,
    useContext,
    useEffect,
    useLayoutEffect,
    useReducer,
} from 'react';

import { useLocation } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import { CookieType } from '../constants/cookieSettings';
import { defaultLocale, LocaleData } from '../constants/locale';
import { getLocaleFromLanguageString } from '../helpers/trans';
import { useCookieSettings } from './CookieContext';

export const LocaleContext = createContext<LocaleData>(defaultLocale);
export const LocaleDispatchContext = createContext((localeData => localeData) as Dispatch<LocaleData>);

export const useLocale = () => useContext(LocaleContext);
export const useLocaleDispatch = () => useContext(LocaleDispatchContext);

const localeReducer: Reducer<LocaleData, LocaleData> = (prevState, localeData) => localeData;

const LocaleProvider: FC<PropsWithChildren> = ({ children }) => {
    const location = useLocation();
    const cookieSettings = useCookieSettings();
    const functionalCookiesAccepted = cookieSettings[CookieType.functional];

    const [storedLocale, storeLocale] = useLocalStorage<LocaleData>('locale', undefined);

    const [locale, dispatch] = useReducer(localeReducer, storedLocale || defaultLocale);

    // Overwrite defaultLocale with routeLocale to allow comparison for client-side locale redirects
    useLayoutEffect((): void => {
        const { pathname } = location;
        const decodedUrl = decodeURIComponent(pathname);

        const routeLanguage = decodedUrl.split('/')[1];
        const routeLocale = getLocaleFromLanguageString(routeLanguage);

        dispatch(routeLocale || defaultLocale);
    }, []);

    useEffect((): void => {
        const { language, direction } = locale;

        if (functionalCookiesAccepted) {
            storeLocale(locale);
        }

        document.documentElement.dir = direction;
        document.documentElement.lang = language;
    }, [locale, functionalCookiesAccepted]);

    return (
        <LocaleContext.Provider value={locale}>
            <LocaleDispatchContext.Provider value={dispatch}>
                {children}
            </LocaleDispatchContext.Provider>
        </LocaleContext.Provider>
    );
};

export default LocaleProvider;
